import { useState, type DragEvent } from "react";
import type { RequestDto } from "../lib/api";
import { displayTitle, formatDuration, formatMoney, thumbnailUrl } from "../lib/youtube";
import { useVideoTitle } from "../hooks/useVideoTitle";
import { useVideoDuration } from "../hooks/useVideoDuration";

/** Pencil — matches the pin icon's stroked-outline style. */
function EditIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <path
        d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/** Speech bubble — `fill` follows CSS, so it fills in once a note exists. */
function NoteIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <path
        d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Checkmark — marks a request done. */
function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <path
        d="M20 6 9 17l-5-5"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/** Curved arrow rounding up and to the right — skip past this request. */
function SkipIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <path
        d="M4 20v-7a4 4 0 0 1 4-4h12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <polyline
        points="15 4 20 9 15 14"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/** Trash can — permanent delete, distinct from the Skip action. */
function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <path
        d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6h16ZM10 11v6M14 11v6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/** Pushpin. `fill` follows CSS, so it reads as an outline until `.pinned`. */
function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
      <path
        d="M12 17v5M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface Props {
  request: RequestDto;
  onComplete?: () => void;
  onSkip?: () => void;
  /** Permanently delete the request (unlike skip, it leaves no history). */
  onRemove?: () => void;
  onTogglePin?: () => void;
  /** Override the matched amount. Receives the new value in cents. */
  onEditAmount?: (amountCents: number) => void;
  /** Set/clear the freeform note. Receives the new text (blank clears it). */
  onEditNotes?: (notes: string) => void;
  /** Correct the request's video link. Receives the new URL. */
  onEditLink?: (youtubeUrl: string) => void;
  /** Return a completed/skipped item to the queue. */
  onReactivate?: () => void;
  busy?: boolean;
  /** Drag-and-drop reordering. Present only for reorderable sections. */
  draggable?: boolean;
  dragging?: boolean;
  /** Show a drop indicator line above / below this card. */
  dropBefore?: boolean;
  dropAfter?: boolean;
  onDragStart?: (e: DragEvent) => void;
  onDragOver?: (e: DragEvent) => void;
  onDrop?: (e: DragEvent) => void;
  onDragEnd?: (e: DragEvent) => void;
}

export function RequestCard({
  request,
  onComplete,
  onSkip,
  onRemove,
  onTogglePin,
  onEditAmount,
  onEditNotes,
  onEditLink,
  onReactivate,
  busy,
  draggable,
  dragging,
  dropBefore,
  dropAfter,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: Props) {
  // Hooks must run unconditionally; harmless (resolve to null) for a title-only request.
  const resolvedTitle = useVideoTitle(request.youtubeUrl ?? "");
  const durationSeconds = useVideoDuration(request.youtubeUrl ?? "");
  const thumb = request.youtubeUrl ? thumbnailUrl(request.youtubeUrl) : null;
  const title = request.youtubeUrl
    ? resolvedTitle ?? displayTitle(request.youtubeUrl)
    : request.title ?? "Untitled request";
  const done = request.section === "completed";
  const pinned = request.manuallyPositioned;

  // Inline amount editor. `draft` holds dollars as typed; commit converts to
  // cents. Opening seeds it from the current amount.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  function openEditor() {
    setDraft((request.matchedCents / 100).toFixed(2));
    setEditing(true);
  }

  function commitEdit() {
    const dollars = Number(draft);
    if (!Number.isFinite(dollars) || dollars < 0) return; // ignore junk input
    const cents = Math.round(dollars * 100);
    if (cents !== request.matchedCents) onEditAmount?.(cents);
    setEditing(false);
  }

  // Inline note editor. Seeds from the current note; a blank draft clears it.
  const NOTES_MAX = 500;
  const [editingNote, setEditingNote] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");

  function openNoteEditor() {
    setNoteDraft(request.notes ?? "");
    setEditingNote(true);
  }

  function commitNote() {
    const next = noteDraft.trim();
    if (next !== (request.notes ?? "")) onEditNotes?.(next);
    setEditingNote(false);
  }

  // Inline link editor. Seeds from the current URL; a blank draft is ignored
  // (the link can be corrected but not cleared).
  const [editingLink, setEditingLink] = useState(false);
  const [linkDraft, setLinkDraft] = useState("");

  function openLinkEditor() {
    setLinkDraft(request.youtubeUrl ?? "");
    setEditingLink(true);
  }

  function commitLink() {
    const next = linkDraft.trim();
    if (next && next !== request.youtubeUrl) onEditLink?.(next);
    setEditingLink(false);
  }

  return (
    <div
      className={
        `card ${request.section}` +
        (dragging ? " dragging" : "") +
        (dropBefore ? " drop-before" : "") +
        (dropAfter ? " drop-after" : "") +
        (busy ? " busy" : "")
      }
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      {busy && (
        <span className="card-spinner-wrap" aria-hidden="true">
          <span className="spinner" />
        </span>
      )}

      {draggable && (
        <span className="grip" aria-hidden="true" title="Drag to reorder">
          ⠿
        </span>
      )}

      <div className="thumb-wrap">
        {thumb ? (
          <img className="thumb" src={thumb} alt="" loading="lazy" />
        ) : (
          <div className="thumb" />
        )}
        {durationSeconds !== null && (
          <span className="thumb-duration">{formatDuration(durationSeconds)}</span>
        )}
      </div>

      <div className="body">
        <div className="title">
          {editingLink ? (
            <div className="link-edit">
              <input
                type="url"
                value={linkDraft}
                autoFocus
                disabled={busy}
                placeholder="https://youtube.com/watch?v=…"
                onChange={(e) => setLinkDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitLink();
                  if (e.key === "Escape") setEditingLink(false);
                }}
                aria-label="Video link"
              />
              <button className="btn" onClick={commitLink} disabled={busy}>
                Save
              </button>
              <button
                className="btn ghost"
                onClick={() => setEditingLink(false)}
                disabled={busy}
              >
                Cancel
              </button>
            </div>
          ) : (
            <>
              {request.youtubeUrl ? (
                <a href={request.youtubeUrl} target="_blank" rel="noreferrer">
                  {title}
                </a>
              ) : (
                title
              )}
              {onEditLink && (
                <button
                  type="button"
                  className="title-edit-btn"
                  onClick={openLinkEditor}
                  disabled={busy}
                  aria-label="Edit video link"
                  title="Correct the video link"
                >
                  <EditIcon />
                </button>
              )}
            </>
          )}
        </div>
        <div className="meta">
          <span className={`badge platform ${request.requester.platform}`}>
            {request.requester.platform}
          </span>
          <span>@{request.requester.username}</span>
          {(request.matchedCents > 0 || onEditAmount) &&
            (editing ? (
              <span className="amount-edit-inline">
                <span className="prefix">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={draft}
                  autoFocus
                  disabled={busy}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitEdit();
                    if (e.key === "Escape") setEditing(false);
                  }}
                  aria-label="Matched amount in dollars"
                />
                <button className="btn" onClick={commitEdit} disabled={busy}>
                  Save
                </button>
                <button
                  className="btn ghost"
                  onClick={() => setEditing(false)}
                  disabled={busy}
                >
                  Cancel
                </button>
              </span>
            ) : (
              <span
                className={`badge amount${onEditAmount ? " editable" : ""}`}
                onClick={!done && onEditAmount ? openEditor : undefined}
                title={!done && onEditAmount ? "Click to edit matched amount" : undefined}
              >
                {formatMoney(request.matchedCents)}
                {!done && onEditAmount && <EditIcon />}
              </span>
            ))}
          {request.carriedOver && <span className="badge platform">carried over</span>}
          {done &&
            (request.status === "skipped" ? (
              <span className="badge skipped">skipped</span>
            ) : (
              <span className="badge completed">completed</span>
            ))}
        </div>

        {editingNote ? (
          <div className="note-edit">
            <textarea
              value={noteDraft}
              autoFocus
              rows={2}
              maxLength={NOTES_MAX}
              disabled={busy}
              placeholder="Add a note…"
              onChange={(e) => setNoteDraft(e.target.value)}
              onKeyDown={(e) => {
                // Enter saves; Shift+Enter inserts a newline.
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  commitNote();
                }
                if (e.key === "Escape") setEditingNote(false);
              }}
              aria-label="Request note"
            />
            <div className="note-edit-actions">
              <button className="btn" onClick={commitNote} disabled={busy}>
                Save
              </button>
              <button
                className="btn ghost"
                onClick={() => setEditingNote(false)}
                disabled={busy}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          request.notes && (
            <div
              className={`note${!done && onEditNotes ? " editable" : ""}`}
              onClick={!done && onEditNotes ? openNoteEditor : undefined}
              title={!done && onEditNotes ? "Click to edit note" : undefined}
            >
              {request.notes}
            </div>
          )
        )}
      </div>

      {!done && (
        <div className="actions">
          {onEditNotes && (
            <button
              className={`btn icon note-btn${request.notes ? " has-note" : ""}`}
              onClick={editingNote ? () => setEditingNote(false) : openNoteEditor}
              disabled={busy}
              aria-pressed={editingNote}
              aria-label={request.notes ? "Edit note" : "Add note"}
              title={request.notes ? "Edit note" : "Add a note"}
            >
              <NoteIcon />
            </button>
          )}
          {onTogglePin && (
            <button
              className={`btn pin-btn${pinned ? " pinned" : ""}`}
              onClick={onTogglePin}
              disabled={busy}
              aria-pressed={pinned}
              aria-label={pinned ? "Unpin request" : "Pin request"}
              title={
                pinned
                  ? "Pinned — automatic ordering leaves it alone. Click to unpin."
                  : "Pin to this slot"
              }
            >
              <PinIcon />
            </button>
          )}
          <button
            className="btn icon success"
            onClick={onComplete}
            disabled={busy}
            aria-label="Mark as done"
            title="Mark as done"
          >
            <CheckIcon />
          </button>
          <button
            className="btn icon danger"
            onClick={onSkip}
            disabled={busy}
            aria-label="Skip request"
            title="Skip this request"
          >
            <SkipIcon />
          </button>
          {onRemove && (
            <button
              className="btn icon danger-solid"
              onClick={() => {
                if (window.confirm("Remove this request? This can't be undone.")) onRemove();
              }}
              disabled={busy}
              aria-label="Remove request"
              title="Remove this request permanently"
            >
              <TrashIcon />
            </button>
          )}
        </div>
      )}

      {done && onReactivate && (
        <div className="actions">
          <button
            className="btn"
            onClick={onReactivate}
            disabled={busy}
            title="Return this request to the queue"
          >
            Requeue
          </button>
        </div>
      )}
    </div>
  );
}
